import { ValidationError } from "sequelize"
import { NONE, VALUE, UNLIMITED } from "const"
import {
    MtAccountPackage,
    MtAccountPackageCreate,
    UpdateSilCalculationsType,
} from "server/models"
import { deepCopy, OrderType } from "client/utils"
import {
    AccountPackageList,
    AccountPackageValidation,
    PackageList,
    PackageUsage,
    LangType,

    LoadAllPackages,
    setSystemPackageUsageSilCalculations,
} from "client/store"
import { GET, POST, sort, compare, validate } from "client/store/utils"
import { Dispatch } from "redux"


export const LoadAllAccountPackages = () => (
    GET("/mtAccountPackage/all")
    .then((rows: MtAccountPackage[]) => {
        const accountPackageList: AccountPackageList = {}
        rows.forEach((row) => {
            if (!accountPackageList[row.accountId]) accountPackageList[row.accountId] = []
            accountPackageList[row.accountId].push(row)
        })
        return accountPackageList
    })
)

export const LoadAccountPackages = (accountId: number) => (
    GET(`/mtAccountPackage/accountPackages/${accountId}`)
    .then((rows: MtAccountPackage[]) => rows)
)

export const LoadAccountPackageById = (id: number, accountId: number) => (
    GET(`/mtAccountPackage/id/${id}/accountId/${accountId}`)
    .then((accountPackage: MtAccountPackage) => accountPackage)
)

export const SaveAccountPackage = (
    accountPackage: MtAccountPackage | MtAccountPackageCreate,
    lang: LangType,
) => (
    POST(
        "/mtAccountPackage/save",
        JSON.stringify(accountPackage),
    ).then((res) => {
        const newAccountPackage = res as MtAccountPackage
        const error = res as ValidationError

        return {
            newAccountPackageId: newAccountPackage.id || null,
            validation: <AccountPackageValidation>validate(error, lang),
        }
    })
)

export const SortAccountPackages = (
    accountPackages: MtAccountPackage[],
    sortColumn: keyof MtAccountPackage,
    sortOrder: OrderType,
    packageList: PackageList,
): MtAccountPackage[] => {
    const newAccountPackages: MtAccountPackage[] = deepCopy(accountPackages)
    if (sortColumn === "packageId") {
        newAccountPackages.sort((a, b) => {
            const A = packageList[a.packageId] ? packageList[a.packageId].name.toUpperCase() : ""
            const B = packageList[b.packageId] ? packageList[b.packageId].name.toUpperCase() : ""

            return compare(A, B, sortOrder)
        })
    } else {
        newAccountPackages.sort((a, b) => sort(a, b, sortColumn, sortOrder))
    }
    return newAccountPackages
}


// package usage


export const LoadAccountPackageUsage = (accountId: number) => (
    LoadAccountPackages(accountId).then((accountPackages) => (
        LoadAllPackages().then((packageList) => {
            if (!accountPackages.length) return null

            const currentPackageRow = accountPackages[0]
            const currentPackage = packageList[currentPackageRow.packageId]

            // if current package has NONE or UNLIMITED calculations
            if (currentPackage) {
                const { silCalculations } = currentPackage.settingsParsed
                let packageUsage: PackageUsage = null

                if (silCalculations === NONE) {
                    packageUsage = {
                        maxCalculations: 0,
                        usedCalculations: currentPackageRow.usedSilCalculations,
                    }
                }
                if (silCalculations === UNLIMITED) {
                    packageUsage = {
                        maxCalculations: null,
                        usedCalculations: currentPackageRow.usedSilCalculations,
                    }
                }
                if (packageUsage) {
                    return {
                        id: currentPackageRow.id,
                        packageUsage,
                    }
                }
            }

            // summarize calculations left
            let maxCalculations = 0
            accountPackages.forEach((row) => {
                if (packageList[row.packageId]) {
                    const { silCalculations, silCalculationsValue } = packageList[row.packageId].settingsParsed
                    if (silCalculations === VALUE && silCalculationsValue) {
                        maxCalculations += silCalculationsValue
                        maxCalculations += row.additionalSilCalculations

                        // used calculations from the current package are not subtracted from the common value
                        if (row.id !== currentPackageRow.id) maxCalculations -= row.usedSilCalculations
                    }
                }
            })

            const packageUsage: PackageUsage = {
                maxCalculations,
                usedCalculations: currentPackageRow.usedSilCalculations,
            }
            return {
                id: currentPackageRow.id,
                packageUsage,
            }
        })
    ))
)

export const IncreaseSilCalculations = (accountId: number, dispatch: Dispatch, calcNumber?: number) => (
    LoadAccountPackageUsage(accountId).then(({ id, packageUsage }) => {
        const silCalculations = packageUsage.usedCalculations + (calcNumber || 1)
        const data: UpdateSilCalculationsType = {
            id,
            silCalculations,
        }

        return POST(
            "/mtAccountPackage/updateUsedSilCalculations",
            JSON.stringify(data),
        ).then(() => {
            dispatch(setSystemPackageUsageSilCalculations(silCalculations))
        })
    })
)
